import { useI18n, useT } from "../../../shared/i18n/i18n";
import { ArrowRight, KeyRound, Mail } from "lucide-react";
import { useSignIn } from "../../../features/auth/model/use-sign-in";
import { oidcStartUrl, readLoginError } from "../../../features/auth/model/oidc-sign-in";
import { useLoginMethods } from "../../../features/auth/model/use-login-methods";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Separator } from "@/components/ui/separator";

type LoginPageProps = {
  invitationToken?: string;
  onSignedIn: () => void;
};

export function LoginPage({ invitationToken, onSignedIn }: LoginPageProps) {
  const t = useT();
  const { locale } = useI18n();
  const methods = useLoginMethods();
  const { code, email, error, isSubmitting, needsCode, setCode, setEmail, submit } = useSignIn({ invitationToken, onSignedIn });
  // Отказ, с которым сервер вернул браузер от провайдера входа.
  const loginError = readLoginError(window.location.search);

  const provider = methods.data?.oidc ?? null;
  // Сервер не ответил, какие способы есть, — показывается прежняя форма по коду.
  const emailCode = methods.data ? methods.data.emailCode : methods.isError;
  const providerOnly = provider !== null && !emailCode;

  return (
    <main className="grid min-h-screen place-items-center px-6">
      <Card className="w-full max-w-md">
        <CardHeader>
          <div className="mb-4 flex size-11 items-center justify-center rounded-md bg-primary text-primary-foreground">
            {providerOnly ? <KeyRound /> : <Mail />}
          </div>
          <CardTitle>
            {invitationToken ? t("login.joinWorkspace") : providerOnly ? t("login.providerOnly") : t("login.signIn")}
          </CardTitle>
          <CardDescription>
            {providerOnly ? t("login.providerHint") : invitationToken ? t("login.joinHint") : t("login.signInHint")}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-col gap-4">
          {loginError && provider ? (
            <Alert variant="destructive">
              <AlertDescription>{t(`login.error.${loginError}`, { provider: provider.label })}</AlertDescription>
            </Alert>
          ) : null}

          {provider ? (
            <Button
              className="w-full"
              onClick={() => window.location.assign(oidcStartUrl({ invitationToken, locale, location: window.location }))}
              type="button"
            >
              {t("login.withProvider", { provider: provider.label })}
              <ArrowRight data-icon="inline-end" />
            </Button>
          ) : null}

          {provider && emailCode ? (
            <div className="flex items-center gap-3 text-xs text-muted-foreground">
              <Separator className="flex-1" />
              {t("login.or")}
              <Separator className="flex-1" />
            </div>
          ) : null}

          {emailCode ? (
            <form className="flex flex-col gap-4" onSubmit={submit}>
              <div className="flex flex-col gap-2">
                <label className="text-sm font-medium" htmlFor="email">
                  {t("login.email")}
                </label>
                <Input
                  id="email"
                  autoComplete="email"
                  disabled={needsCode || isSubmitting}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder={t("login.emailPlaceholder")}
                  type="email"
                  value={email}
                />
              </div>

              {needsCode ? (
                <div className="flex flex-col gap-2">
                  <label className="text-sm font-medium" htmlFor="code">
                    {t("login.emailCode")}
                  </label>
                  <Input
                    id="code"
                    inputMode="numeric"
                    onChange={(event) => setCode(event.target.value)}
                    placeholder="000000"
                    value={code}
                  />
                </div>
              ) : null}

              {error ? (
                <Alert variant="destructive">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              ) : null}

              <Button className="w-full" disabled={isSubmitting || !email} type="submit" variant={provider ? "outline" : "default"}>
                {isSubmitting ? t("common.loading") : needsCode ? t("login.verifyCode") : t("login.continue")}
                <ArrowRight data-icon="inline-end" />
              </Button>
            </form>
          ) : null}
        </CardContent>
      </Card>
    </main>
  );
}
